import { useState, useCallback, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, Leaf, Droplets, FlaskConical, Check, RotateCcw, Beaker, AlertCircle, Sparkles, ChevronRight, HelpCircle, Shield, AlertTriangle } from 'lucide-react';
import { ITEMS } from '@/config/constants';
import LeonDialogue from '@/components/LeonDialogue';
import CONFIG from '@/config/siteConfig';
import { HERB_MIXING } from '@/config/missionContent';

const HERBS = [
  { id: 'green', label: 'Green Herb', shortLabel: 'G', color: '#22c55e', bgGlow: 'rgba(34,197,94,0.15)', Icon: Leaf, effect: 'HEALING' },
  { id: 'red', label: 'Red Herb', shortLabel: 'R', color: '#ef4444', bgGlow: 'rgba(239,68,68,0.15)', Icon: Heart, effect: 'POWER' },
  { id: 'blue', label: 'Blue Herb', shortLabel: 'B', color: '#3b82f6', bgGlow: 'rgba(59,130,246,0.15)', Icon: Droplets, effect: 'CURE' },
];

const RECIPES = [
  { name: 'Health Booster', recipe: ['green', 'green', 'red'], hint: 'Healing is the foundation, but it needs a spark of power to activate' },
  { name: 'Antidote Serum', recipe: ['blue', 'green', 'blue'], hint: 'The cure must be dominant, with just enough life force to bind it' },
  { name: 'Full Recovery Mix', recipe: ['green', 'red', 'blue'], hint: 'Balance is key -- every element has its place in the chain' },
];


/* ─── Memoized Lab Background ─── */
function LabBackground() {
  const bubbles = useMemo(() =>
    Array.from({ length: 12 }, (_, i) => ({
      id: i,
      left: `${10 + (i * 7.8) % 80}%`,
      size: 3 + (i % 4) * 1.5,
      duration: 6 + (i % 5) * 2,
      delay: i * 0.6,
      color: ['#22c55e', '#ef4444', '#3b82f6'][i % 3],
      opacity: 0.12 + (i % 3) * 0.06,
    })), []);

  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none">
      {/* Dark chemical gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-[#020508] to-black" />

      {/* Hexagonal grid */}
      <div className="absolute inset-0 opacity-[0.04]"
        style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 5 L55 20 L55 40 L30 55 L5 40 L5 20 Z' fill='none' stroke='%2300ff41' stroke-width='0.5'/%3E%3C/svg%3E")` }}
      />

      {/* Memoized floating bubbles */}
      {bubbles.map(b => (
        <motion.div key={b.id} className="absolute bottom-0"
          style={{ left: b.left }}
          animate={{ y: [0, -400, -600], opacity: [0, b.opacity, 0], scale: [0.5, 1, 0.3] }}
          transition={{ duration: b.duration, delay: b.delay, repeat: Infinity, ease: 'easeOut' }}
        >
          <div className="rounded-full border"
            style={{ width: b.size, height: b.size, borderColor: `${b.color}30`, background: `${b.color}08` }}
          />
        </motion.div>
      ))}

      {/* Chemical-green vignette */}
      <div className="absolute inset-0 animate-pulse" style={{ animationDuration: '6s', background: 'radial-gradient(ellipse at center, transparent 40%, rgba(0,40,20,0.2) 100%)' }} />

      {/* CRT scanlines */}
      <div className="absolute inset-0 opacity-[0.015]" style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,255,65,0.12) 2px, rgba(0,255,65,0.12) 4px)', backgroundSize: '100% 4px' }} />

      {/* Toxic mist at bottom */}
      <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-green-950/10 to-transparent" />
    </div>
  );
}

/* ─── Memoized Bubble Effect ─── */
function BubbleEffect({ active, color }) {
  const particles = useMemo(() =>
    Array.from({ length: 6 }, (_, i) => ({
      id: i,
      x: `${15 + (i * 14) % 70}%`,
      delay: i * 0.15,
      duration: 0.8 + (i % 3) * 0.3,
      repeatDelay: (i % 4) * 0.4,
    })), []);

  if (!active) return null;
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none rounded-lg">
      {particles.map(p => (
        <motion.div key={p.id}
          initial={{ y: '100%', x: p.x, opacity: 0 }}
          animate={{ y: '-20%', opacity: [0, 0.5, 0] }}
          transition={{ duration: p.duration, delay: p.delay, repeat: Infinity, repeatDelay: p.repeatDelay }}
          className="absolute w-1.5 h-1.5 rounded-full"
          style={{ background: color || '#22c55e40' }}
        />
      ))}
    </div>
  );
}

/* ─── HUD Corners ─── */
function HUDCorners() {
  return (
    <>
      {[['top-0 left-0', 'border-t border-l'], ['top-0 right-0', 'border-t border-r'], ['bottom-0 left-0', 'border-b border-l'], ['bottom-0 right-0', 'border-b border-r']].map(([pos, borders]) => (
        <div key={pos} className={`absolute ${pos} w-4 h-4 ${borders} border-green-700/20 pointer-events-none`} />
      ))}
    </>
  );
}


/* ─── Tutorial Screen ─── */
function LabTutorial({ onStart }) {
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
      className="relative z-10 max-w-lg mx-auto space-y-4">
      {/* Tutorial header */}
      <div className="text-center">
        <div className="flex items-center justify-center gap-2 mb-2">
          <HelpCircle size={14} className="text-green-500" />
          <span className="text-xs font-terminal tracking-[5px] text-green-500 uppercase">Lab Protocol</span>
          <HelpCircle size={14} className="text-green-500" />
        </div>
        <p className="text-xs font-terminal text-green-700 tracking-wider">Review chemical compounds before synthesis</p>
      </div>

      {/* Herb reference */}
      <div className="bg-black/40 border border-green-900/30 rounded p-4 space-y-3"
        style={{ boxShadow: 'inset 0 0 20px rgba(0,0,0,0.3)' }}>
        <div className="flex items-center gap-2 pb-2 border-b border-green-900/20">
          <Leaf size={12} className="text-green-600" />
          <span className="text-xs font-terminal tracking-[3px] text-green-600 uppercase">Available Herbs</span>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {HERBS.map((herb, i) => (
            <motion.div key={herb.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 + i * 0.1 }}
              className="text-center p-2.5 rounded border bg-black/30"
              style={{ borderColor: `${herb.color}25` }}>
              <herb.Icon size={22} className="mx-auto mb-1.5" style={{ color: herb.color, filter: `drop-shadow(0 0 6px ${herb.color}40)` }} />
              <p className="text-xs font-terminal tracking-wider" style={{ color: herb.color }}>{herb.label}</p>
              <p className="text-xs font-terminal text-gray-600 mt-0.5">{herb.effect}</p>
              <span className="inline-block mt-1 text-xs font-orbitron font-bold" style={{ color: herb.color }}>{herb.shortLabel}</span>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Compounds to create */}
      <div className="bg-black/40 border border-green-900/30 rounded p-4 space-y-3"
        style={{ boxShadow: 'inset 0 0 20px rgba(0,0,0,0.3)' }}>
        <div className="flex items-center gap-2 pb-2 border-b border-green-900/20">
          <FlaskConical size={12} className="text-yellow-600" />
          <span className="text-xs font-terminal tracking-[3px] text-yellow-600 uppercase">Compounds to Create</span>
        </div>
        <div className="space-y-2">
          {RECIPES.map((r, i) => (
            <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 + i * 0.12 }}
              className="flex items-center justify-between p-2 rounded border border-green-900/15 bg-black/20">
              <div className="flex items-center gap-2">
                <span className="text-xs font-terminal text-gray-600 w-4">{i + 1}.</span>
                <span className="text-xs font-terminal text-yellow-400 tracking-wider">{r.name}</span>
              </div>
              <span className="text-xs font-terminal text-gray-600 tracking-wider">{r.recipe.length} HERBS</span>
            </motion.div>
          ))}
        </div>
      </div>

      {/* How it works */}
      <div className="bg-black/40 border border-green-900/30 rounded p-4 space-y-3"
        style={{ boxShadow: 'inset 0 0 20px rgba(0,0,0,0.3)' }}>
        <div className="flex items-center gap-2 pb-2 border-b border-green-900/20">
          <AlertTriangle size={12} className="text-green-600" />
          <span className="text-xs font-terminal tracking-[3px] text-green-600 uppercase">Procedure</span>
        </div>
        <div className="space-y-2">
          {[
            { step: '01', text: 'Click herbs from the shelf to add them to mixing slots' },
            { step: '02', text: 'Order matters — add herbs in the exact sequence shown' },
            { step: '03', text: 'Click Synthesize to test your mixture' },
          ].map((item, i) => (
            <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.8 + i * 0.1 }}
              className="flex items-center gap-3">
              <span className="text-xs font-terminal text-green-700 w-5 flex-shrink-0">{item.step}</span>
              <span className="text-xs font-terminal text-gray-400 tracking-wider">{item.text}</span>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Start button */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1 }}
        className="text-center pt-2">
        <motion.button
          whileHover={{ scale: 1.03, boxShadow: '0 0 25px rgba(34,197,94,0.2)' }}
          whileTap={{ scale: 0.97 }}
          onClick={onStart}
          className="group relative px-10 py-3 bg-gradient-to-r from-green-950/50 via-green-900/30 to-green-950/50 border-2 border-green-600/60 text-green-400 font-terminal text-base tracking-[5px] uppercase transition-all hover:border-green-500/80 overflow-hidden"
        >
          <motion.div className="absolute inset-0 bg-gradient-to-r from-transparent via-green-500/8 to-transparent pointer-events-none"
            animate={{ x: ['-200%', '200%'] }} transition={{ duration: 2.5, repeat: Infinity, repeatDelay: 0.5 }} />
          <motion.div className="absolute inset-0 border border-green-500/20 rounded-sm pointer-events-none"
            animate={{ opacity: [0, 0.5, 0] }} transition={{ duration: 2, repeat: Infinity }} />
          <span className="relative z-10 flex items-center justify-center gap-2">
            <FlaskConical size={14} /> Begin Synthesis <ChevronRight size={14} />
          </span>
        </motion.button>
      </motion.div>
    </motion.div>
  );
}

/* ─── Main Component ─── */
export default function HerbMixing({ onComplete, onItemCollected }) {
  const [showLeon, setShowLeon] = useState(true);
  const [showTutorial, setShowTutorial] = useState(false);
  const [active, setActive] = useState(false);
  const [currentRecipe, setCurrentRecipe] = useState(0);
  const [mixture, setMixture] = useState([]);
  const [solved, setSolved] = useState([]);
  const [showResult, setShowResult] = useState(null);
  const [showFinalLeon, setShowFinalLeon] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [shaking, setShaking] = useState(false);
  const [showFlash, setShowFlash] = useState(null);
  const [successPhase, setSuccessPhase] = useState(0);
  const [showProceed, setShowProceed] = useState(false);

  // Mount-reset
  useEffect(() => {
    setShowLeon(true);
    setShowTutorial(false);
    setActive(false);
    setCurrentRecipe(0);
    setMixture([]);
    setSolved([]);
    setShowResult(null);
    setShowFinalLeon(false);
    setShowSuccess(false);
    setShaking(false);
    setShowFlash(null);
    setSuccessPhase(0);
    setShowProceed(false);
  }, []);

  const handleLeonDismiss = useCallback(() => {
    setShowLeon(false);
    setShowTutorial(true);
  }, []);

  const handleTutorialStart = useCallback(() => {
    setShowTutorial(false);
    setActive(true);
  }, []);

  const recipe = RECIPES[currentRecipe];
  const maxIngredients = recipe ? recipe.recipe.length : 0;

  const handleAddHerb = useCallback((herbId) => {
    if (!active || mixture.length >= maxIngredients || showResult) return;
    setMixture(prev => [...prev, herbId]);
  }, [active, mixture.length, maxIngredients, showResult]);

  const handleMix = useCallback(() => {
    if (!active || mixture.length !== maxIngredients) return;
    const correct = mixture.every((h, i) => h === recipe.recipe[i]);
    if (correct) {
      setShowResult('success');
      setShowFlash('green');
      setTimeout(() => setShowFlash(null), 150);
      const newSolved = [...solved, currentRecipe];
      setSolved(newSolved);
      setTimeout(() => {
        if (newSolved.length === RECIPES.length) {
          setActive(false);
          setShowSuccess(true);
          setShowResult(null);
          // Multi-phase success
          setSuccessPhase(1);
          setTimeout(() => setSuccessPhase(2), 500);
          setTimeout(() => setSuccessPhase(3), 1200);
          setTimeout(() => setSuccessPhase(4), 1800);
          setTimeout(() => setShowFinalLeon(true), 2200);
        } else {
          setCurrentRecipe(newSolved.length);
          setMixture([]);
          setShowResult(null);
        }
      }, 1200);
    } else {
      setShowResult('fail');
      setShowFlash('red');
      setTimeout(() => setShowFlash(null), 150);
      setShaking(true);
      setTimeout(() => setShaking(false), 400);
      setTimeout(() => {
        setMixture([]);
        setShowResult(null);
      }, 1000);
    }
  }, [active, mixture, maxIngredients, recipe, solved, currentRecipe]);

  const handleReset = useCallback(() => {
    if (showResult) return;
    setMixture([]);
  }, [showResult]);

  const handleFinalDismiss = useCallback(() => {
    setShowFinalLeon(false);
    onItemCollected?.(ITEMS.FIRST_AID_KIT);
    setShowProceed(true);
  }, [onItemCollected]);

  return (
    <div className="puzzle-container relative overflow-hidden" data-testid="puzzle-herb-mixing">
      <LabBackground />

      {/* Flash overlay */}
      <AnimatePresence>
        {showFlash && (
          <motion.div initial={{ opacity: 0.4 }} animate={{ opacity: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}
            className={`absolute inset-0 pointer-events-none z-50 ${showFlash === 'green' ? 'bg-green-500/10' : 'bg-red-500/10'}`} />
        )}
      </AnimatePresence>

      {/* Success shockwave */}
      <AnimatePresence>
        {successPhase >= 1 && (
          <>
            <motion.div initial={{ opacity: 0.6 }} animate={{ opacity: 0 }} transition={{ duration: 1 }}
              className="absolute inset-0 bg-green-500/10 pointer-events-none z-40" />
            <motion.div
              initial={{ width: 0, height: 0, opacity: 0.5 }}
              animate={{ width: 600, height: 600, opacity: 0 }}
              transition={{ duration: 1.2, ease: 'easeOut' }}
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-green-400/40 pointer-events-none z-40"
            />
          </>
        )}
      </AnimatePresence>

      {/* HUD Top Bar */}
      {(active || showSuccess) && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="relative z-10 flex items-center justify-between mb-3 px-1">
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
            <span className="text-xs font-terminal tracking-[3px] text-green-600 uppercase">Checkpoint 04 // Chemical Lab</span>
          </div>
          <div className="flex items-center gap-2">
            <AlertCircle size={10} className="text-yellow-700" />
            <span className="text-xs font-terminal tracking-wider text-yellow-700 uppercase">Biohazard Level: High</span>
          </div>
        </motion.div>
      )}

      {/* Header */}
      <div className="text-center mb-4 relative z-10">
        <div className="flex items-center justify-center gap-2 mb-2">
          <div className="w-8 h-px bg-gradient-to-r from-transparent to-green-700/40" />
          <Beaker size={12} className="text-green-600" />
          <p className="text-xs font-terminal tracking-[6px] text-green-600 uppercase text-glow-green">Checkpoint 04</p>
          <Beaker size={12} className="text-green-600" />
          <div className="w-8 h-px bg-gradient-to-l from-transparent to-green-700/40" />
        </div>
        <h2 className="font-orbitron text-2xl tracking-wider text-gray-200 uppercase">Chemical Lab</h2>
        <p className="text-sm text-green-700 font-terminal mt-1">Herb Synthesis // Mix the correct compounds</p>
      </div>

      {/* Tutorial screen */}
      <AnimatePresence>
        {showTutorial && <LabTutorial onStart={handleTutorialStart} />}
      </AnimatePresence>

      {/* Progress pills */}
      {(active || showSuccess) && (
        <div className="flex justify-center gap-2 mb-4 relative z-10">
          {RECIPES.map((r, i) => (
            <motion.div
              key={i}
              animate={i === currentRecipe && active ? { borderColor: ['rgba(234,179,8,0.3)', 'rgba(234,179,8,0.7)', 'rgba(234,179,8,0.3)'] } : {}}
              transition={i === currentRecipe ? { duration: 2, repeat: Infinity } : {}}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded border text-xs font-terminal tracking-wider transition-all
                ${i < solved.length ? 'bg-green-950/30 border-green-600/50 text-green-400' : i === currentRecipe ? 'border-yellow-600/40 text-yellow-500 bg-yellow-950/10' : 'border-gray-800 text-gray-600'}`}
            >
              {i < solved.length ? <Check size={10} /> : <FlaskConical size={10} />}
              <span>{r.name}</span>
            </motion.div>
          ))}
        </div>
      )}

      {/* Mixture display bar */}
      {active && !showSuccess && recipe && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-center mb-2 relative z-10">
          <div className="inline-flex items-center gap-1 bg-black/50 rounded px-3 py-1 border border-green-900/20">
            <span className="text-xs font-terminal text-green-800 tracking-wider mr-1">MIX:</span>
            {Array.from({ length: maxIngredients }).map((_, i) => {
              const herb = HERBS.find(h => h.id === mixture[i]);
              return (
                <span key={i} className="font-orbitron text-sm w-4 text-center"
                  style={{ color: herb ? herb.color : '#333', textShadow: herb ? `0 0 4px ${herb.color}40` : 'none' }}>
                  {herb ? herb.shortLabel : '_'}
                </span>
              );
            })}
          </div>
        </motion.div>
      )}

      {active && !showSuccess && recipe && (
        <motion.div key={currentRecipe} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} className="space-y-4 max-w-lg mx-auto relative z-10">
          {/* Recipe card */}
          <div className="hud-bracket bg-black/50 p-5 relative overflow-hidden"
            style={{ boxShadow: `0 0 15px ${HERBS.find(h => h.id === recipe.recipe[0])?.bgGlow || 'rgba(0,255,65,0.05)'}` }}>
            <HUDCorners />

            <div className="flex items-center gap-2 mb-3">
              <FlaskConical size={14} className="text-yellow-600" />
              <span className="text-xs font-terminal tracking-[3px] text-yellow-600 uppercase">Formula {currentRecipe + 1}/{RECIPES.length}</span>
              <div className="flex-1 h-px bg-yellow-900/20 ml-2" />
            </div>

            <div className="text-center mb-3">
              <h3 className="font-orbitron text-lg text-yellow-400 tracking-wider">{recipe.name}</h3>
              <p className="text-xs font-terminal text-gray-600 mt-1">{maxIngredients} herbs required</p>
            </div>

            {/* Cryptic hint */}
            <div className="bg-black/40 rounded px-4 py-2.5 border-l-2 border-green-700/30">
              <p className="text-xs font-terminal text-green-800 tracking-wider mb-0.5">LAB NOTES:</p>
              <p className="text-sm font-special text-gray-300 italic">"{recipe.hint}"</p>
            </div>
          </div>

          {/* Mixing station */}
          <div className="relative">
            <div className={`flex justify-center gap-4 p-6 rounded-lg border-2 transition-all duration-500 relative overflow-hidden ${shaking ? 'animate-shake' : ''}
              ${showResult === 'success' ? 'border-green-500/60 bg-green-950/15' : showResult === 'fail' ? 'border-red-500/60 bg-red-950/15' : 'border-gray-700/40 bg-black/30'}`}
              style={{ boxShadow: showResult === 'success' ? '0 0 20px rgba(34,197,94,0.1)' : showResult === 'fail' ? '0 0 20px rgba(239,68,68,0.1)' : 'inset 0 0 20px rgba(0,0,0,0.3)' }}
            >
              <BubbleEffect active={showResult === 'success'} color="#22c55e50" />
              {showResult === 'fail' && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="absolute inset-0 bg-red-500/5 pointer-events-none" />
              )}

              {Array.from({ length: maxIngredients }).map((_, i) => {
                const herb = HERBS.find(h => h.id === mixture[i]);
                const isNextSlot = i === mixture.length && !showResult;
                return (
                  <motion.div
                    key={i}
                    animate={herb && showResult === 'success' ? { y: [0, -4, 0] } : {}}
                    transition={herb ? { duration: 0.5, delay: i * 0.1 } : {}}
                    className="flex flex-col items-center gap-1.5"
                  >
                    <div className={`w-16 h-20 rounded-lg border-2 flex items-center justify-center transition-all relative overflow-hidden ${isNextSlot ? 'animate-pulse' : ''}`}
                      style={{
                        borderColor: herb ? herb.color : isNextSlot ? 'rgba(0,255,65,0.3)' : '#333',
                        borderStyle: herb ? 'solid' : 'dashed',
                        background: herb ? herb.bgGlow : isNextSlot ? 'rgba(0,255,65,0.03)' : 'transparent',
                        boxShadow: herb ? `inset 0 0 15px ${herb.color}15` : isNextSlot ? 'inset 0 0 10px rgba(0,255,65,0.05)' : 'none',
                        animationDuration: '2s',
                      }}
                    >
                      {herb ? (
                        <motion.div initial={{ scale: 0, rotate: -20 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: 'spring', stiffness: 400, damping: 15 }}>
                          <herb.Icon size={30} style={{ color: herb.color, filter: `drop-shadow(0 0 8px ${herb.color}60)` }} />
                        </motion.div>
                      ) : (
                        <div className="flex flex-col items-center gap-1">
                          <FlaskConical size={18} className="text-gray-700" />
                          <span className="text-xs font-terminal text-gray-700">SLOT {i + 1}</span>
                        </div>
                      )}
                      {/* Fill level indicator */}
                      {herb && (
                        <motion.div
                          initial={{ height: 0 }}
                          animate={{ height: '100%' }}
                          transition={{ duration: 0.3 }}
                          className="absolute inset-x-0 bottom-0 rounded-b-lg pointer-events-none"
                          style={{ background: `linear-gradient(to top, ${herb.color}15, transparent)` }}
                        />
                      )}
                    </div>
                    {herb && <span className="text-xs font-terminal tracking-wider" style={{ color: herb.color }}>{herb.effect}</span>}
                  </motion.div>
                );
              })}
            </div>

            {/* Status label */}
            <div className="text-center mt-2">
              <AnimatePresence mode="wait">
                <motion.span
                  key={showResult || 'idle'}
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -5 }}
                  className={`text-xs font-terminal tracking-[3px] uppercase
                    ${showResult === 'success' ? 'text-green-400' : showResult === 'fail' ? 'text-red-400' : 'text-gray-600'}`}
                >
                  {showResult === 'success' ? 'COMPOUND STABLE' : showResult === 'fail' ? 'UNSTABLE -- RESTARTING' : `${mixture.length}/${maxIngredients} INGREDIENTS`}
                </motion.span>
              </AnimatePresence>
            </div>
          </div>

          {/* Herb shelf */}
          <div>
            <p className="text-center text-xs font-terminal text-green-800 tracking-[3px] uppercase mb-3">Chemical Shelf</p>
            <div className="flex justify-center gap-4">
              {HERBS.map(herb => {
                const canAdd = mixture.length < maxIngredients && !showResult;
                return (
                  <motion.button
                    key={herb.id}
                    whileHover={canAdd ? { scale: 1.06, y: -3 } : {}}
                    whileTap={canAdd ? { scale: 0.94 } : {}}
                    onClick={() => handleAddHerb(herb.id)}
                    disabled={!canAdd}
                    className="flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all relative overflow-hidden group"
                    style={{
                      borderColor: canAdd ? `${herb.color}40` : `${herb.color}15`,
                      opacity: canAdd ? 1 : 0.3,
                      filter: canAdd ? 'none' : 'grayscale(0.5)',
                    }}
                    data-testid={`herb-${herb.id}`}
                  >
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/20 pointer-events-none" />
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
                      style={{ background: `radial-gradient(circle at 50% 80%, ${herb.bgGlow}, transparent 70%)` }} />
                    <herb.Icon size={32} style={{ color: herb.color, filter: `drop-shadow(0 0 6px ${herb.color}40)` }} className="relative z-10" />
                    <span className="text-xs font-terminal tracking-wider relative z-10" style={{ color: herb.color }}>{herb.label.toUpperCase()}</span>
                    <span className="text-xs font-terminal tracking-wider text-gray-600 relative z-10">{herb.effect}</span>
                  </motion.button>
                );
              })}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-center gap-4">
            <button onClick={handleReset} className="text-xs text-gray-600 hover:text-gray-400 font-terminal tracking-wider transition-colors flex items-center gap-1" data-testid="reset-mixture-btn">
              <RotateCcw size={10} /> RESET
            </button>
            <motion.button
              whileHover={mixture.length === maxIngredients && !showResult ? { scale: 1.02, boxShadow: '0 0 20px rgba(34,197,94,0.2)' } : {}}
              whileTap={mixture.length === maxIngredients && !showResult ? { scale: 0.98 } : {}}
              onClick={handleMix}
              disabled={mixture.length !== maxIngredients || !!showResult}
              className={`group relative px-8 py-2.5 border font-terminal text-sm tracking-[4px] uppercase transition-all overflow-hidden
                ${mixture.length === maxIngredients && !showResult ? 'bg-green-900/20 border-green-500/60 text-green-400 hover:border-green-500/80' : 'bg-gray-800/20 border-gray-700 text-gray-600 cursor-not-allowed'}`}
              data-testid="mix-btn"
            >
              {mixture.length === maxIngredients && !showResult && (
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-green-500/8 to-transparent pointer-events-none"
                  animate={{ x: ['-200%', '200%'] }}
                  transition={{ duration: 2.5, repeat: Infinity, repeatDelay: 0.5 }}
                />
              )}
              <span className="relative z-10 flex items-center gap-2">
                <Sparkles size={14} /> Synthesize
              </span>
            </motion.button>
          </div>
        </motion.div>
      )}

      {/* Multi-phase success screen */}
      {showSuccess && (
        <div className="text-center space-y-4 relative z-10">
          {/* Phase 2: Icon */}
          <AnimatePresence>
            {successPhase >= 2 && (
              <motion.div initial={{ opacity: 0, scale: 0.3 }} animate={{ opacity: 1, scale: 1 }}
                transition={{ type: 'spring', stiffness: 300, damping: 15 }}
                className="relative w-20 h-20 mx-auto">
                <motion.div animate={{ rotate: 360 }} transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
                  className="absolute inset-0 border-2 border-dashed border-green-500/20 rounded-full" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-16 h-16 rounded-full bg-green-900/30 border-2 border-green-500 flex items-center justify-center"
                    style={{ boxShadow: '0 0 30px rgba(0,255,65,0.25)' }}>
                    <Heart size={28} className="text-green-400" />
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Phase 3: Title */}
          <AnimatePresence>
            {successPhase >= 3 && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <motion.p initial={{ letterSpacing: '2px' }} animate={{ letterSpacing: '6px' }}
                  transition={{ duration: 0.8 }}
                  className="font-orbitron text-green-400 text-sm">
                  ALL COMPOUNDS SYNTHESIZED
                </motion.p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Phase 4: Summary */}
          <AnimatePresence>
            {successPhase >= 4 && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                className="space-y-3">
                <div className="inline-flex flex-col gap-1 bg-green-950/20 border border-green-800/20 rounded px-4 py-2">
                  {RECIPES.map((r, i) => (
                    <motion.div key={i} initial={{ opacity: 0, x: -5 }} animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.15 }}
                      className="flex items-center gap-2">
                      <Check size={10} className="text-green-500" />
                      <span className="text-xs font-terminal text-green-600 tracking-wider">{r.name}</span>
                    </motion.div>
                  ))}
                </div>
                <p className="text-gray-400 font-rajdhani">
                  <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
                    className="text-yellow-500/80" style={{ textShadow: '0 0 8px rgba(234,179,8,0.3)' }}>
                    First Aid Kit acquired
                  </motion.span>
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {showProceed && (
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 200, damping: 20 }}
          className="text-center mt-8 relative z-20"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => onComplete?.()}
            className="inline-flex items-center gap-2 px-8 py-3 rounded font-orbitron text-sm tracking-widest text-black relative overflow-hidden"
            style={{ background: 'linear-gradient(135deg, #22c55e, #00ff41)' }}
          >
            <span className="relative z-10">PROCEED</span>
            <motion.div
              className="absolute inset-0"
              animate={{ x: ['-100%', '200%'] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
              style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent)', width: '50%' }}
            />
          </motion.button>
        </motion.div>
      )}

      {showLeon && (
        <LeonDialogue
          message={HERB_MIXING.leon.intro}
          onDismiss={handleLeonDismiss}
        />
      )}

      {showFinalLeon && (
        <LeonDialogue
          message={HERB_MIXING.leon.complete}
          onDismiss={handleFinalDismiss}
        />
      )}

      {/* Shake keyframes (CSS) */}
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          15% { transform: translateX(-4px); }
          30% { transform: translateX(4px); }
          45% { transform: translateX(-3px); }
          60% { transform: translateX(3px); }
          75% { transform: translateX(-1px); }
          90% { transform: translateX(1px); }
        }
        .animate-shake {
          animation: shake 0.4s ease-out;
        }
      `}</style>
    </div>
  );
}
